import React, { useState } from 'react';
import { useAuth } from '../lib/auth';
import { useClaimsEnabled } from '../lib/features';
import { useStediAnalytics, useStediTrends, useDenialPatterns } from '../api/hooks';
import { money, pct } from '../lib/data';
import PageHeader from '../components/PageHeader';
import AgentChip from '../components/AgentChip';
import { Button } from '../components/ui/button';
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip,
} from 'recharts';
import {
  Sparkles, Loader2, RefreshCw, Zap, ArrowRight, ShieldCheck, Network as NetworkIcon,
  DollarSign, FileWarning, Bot, Lightbulb,
} from 'lucide-react';
import { toast } from '../lib/aiToast';

const RANGES = [{ k: 6, label: '6 wks' }, { k: 12, label: '12 wks' }, { k: 24, label: '24 wks' }];
const NET = [{ key: 'in', label: 'In-network', color: '#10b981' }, { key: 'out', label: 'Out-of-network', color: '#f43f5e' }, { key: 'unknown', label: 'Unknown', color: '#f59e0b' }];
const PLAN_COLORS = ['#0e7490', '#0e92a8', '#27b2c6', '#8b5cf6', '#0ea5e9', '#10b981', '#f59e0b'];
const tip = { borderRadius: 10, border: '1px solid #e8eaef', boxShadow: '0 10px 24px -12px rgba(16,24,40,.25)', fontSize: 12, padding: '8px 10px' };
const axis = { fontSize: 11, fill: '#94a3b8' };

// Data story — the one-line takeaway that turns a chart into an insight.
// Sits inside the chart card so the reader gets the "so what" for free.
function Story({ children }) {
  return (
    <div className="mt-3 pt-3 border-t border-slate-100 flex items-start gap-2">
      <Lightbulb className="h-3.5 w-3.5 text-amber-500 mt-0.5 shrink-0" />
      <p className="text-[13px] text-slate-600 leading-snug">{children}</p>
    </div>
  );
}

// Compact KPI card with an inline recharts sparkline (Uber "metric + trend").
function Metric({ label, value, sub, data, dataKey, color = '#0e7490', tone = 'text-slate-900' }) {
  return (
    <div className="dh-card p-4">
      <div className="text-xs text-slate-500">{label}</div>
      <div className={`mt-1 text-2xl font-semibold tnum ${tone}`}>{value}</div>
      <div className="text-xs text-slate-400 mt-0.5">{sub}</div>
      {data && (
        <div className="h-9 mt-1.5 -mb-1">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 2, right: 0, bottom: 0, left: 0 }}>
              <defs><linearGradient id={`sp-${label}`} x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={color} stopOpacity={0.25} /><stop offset="100%" stopColor={color} stopOpacity={0} /></linearGradient></defs>
              <Area type="monotone" dataKey={dataKey} stroke={color} strokeWidth={2} fill={`url(#sp-${label})`} dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

export default function Insights() {
  const { session } = useAuth();
  const claimsEnabled = useClaimsEnabled();
  const practiceId = session?.practice?.id || 'prac_001';
  const [weeks, setWeeks] = useState(12);
  const { data: a = {} } = useStediAnalytics(practiceId);
  const { net = {}, netPct = {}, planTypes = {}, byPayer = [] } = a;
  const { data: trend = [] } = useStediTrends(practiceId, weeks);
  const { data: patterns = [] } = useDenialPatterns(practiceId);

  const [running, setRunning] = useState(false);
  const [applied, setApplied] = useState({});
  // Default narrative is derived from loaded analytics; runAgent overrides it.
  const [override, setOverride] = useState(null);
  const narrative = override
    || `Across your ${a.total || 0} most-recent eligibility responses: ${pct(a.activeRate)} returned active coverage and ${pct(a.autoClearRate)} cleared with no human review. ${pct(netPct.out)} came back out-of-network and ${pct(a.referralRate)} require a referral — the main drivers of front-desk work. Average individual deductible is ${money(a.avgDeductible)}.`;
  const runAgent = async () => {
    setRunning(true);
    await new Promise((r) => setTimeout(r, 1400));
    setOverride(`Updated scan. Cigna drove the most "unknown network" responses — add those plans to your contracted-plans DB to push auto-clear past ${pct(a.autoClearRate)}. Medicare shows the highest prior-auth rate; pre-stage ABNs. ${a.mismatchCount} demographic mismatches found vs. payer records — worth a data-sync sweep.`);
    setRunning(false); toast.success('Retrospective Agent finished');
  };
  const applyRule = (id) => { setApplied((x) => ({ ...x, [id]: true })); toast.success('Added as an automation rule'); };

  const planData = Object.entries(planTypes).sort((x, y) => y[1] - x[1]).map(([name, value]) => ({ name, value }));
  const netData = NET.map((s) => ({ ...s, value: net[s.key] }));

  // ---- Derived "data stories": the key point each chart is making ----
  const t0 = trend[0] || {}; const tN = trend[trend.length - 1] || {};
  const autoDelta = Math.round((tN.autoPct || 0) - (t0.autoPct || 0));
  const avgAttention = trend.length ? Math.round(trend.reduce((s, t) => s + (t.attention || 0), 0) / trend.length) : 0;

  const totalPlans = planData.reduce((s, d) => s + d.value, 0) || 1;
  const topPlan = planData[0];
  const secondPlan = planData[1];
  const topShare = topPlan ? Math.round((topPlan.value / totalPlans) * 100) : 0;
  const gatekeeperShare = Math.round((((planTypes.HMO || 0) + (planTypes.EPO || 0)) / totalPlans) * 100);

  const riskShare = (netPct.out || 0) + (netPct.unknown || 0);
  const effort = (p) => (p.oonRate || 0) + (p.refRate || 0) + (p.authRate || 0);
  const worstPayer = [...byPayer].sort((x, y) => effort(y) - effort(x))[0];

  return (
    <div className="space-y-5">
      <PageHeader eyebrow="Intelligence" title="Eligibility intelligence" subtitle="Analytics curated from your Stedi 271 responses over time — aggregate only, no PHI.">
        <div className="seg mr-1">{RANGES.map((r) => <button key={r.k} className={weeks === r.k ? 'active' : ''} onClick={() => setWeeks(r.k)}>{r.label}</button>)}</div>
        <Button onClick={runAgent} disabled={running} className="bg-brand-600 hover:bg-brand-700 text-white">
          {running ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Running…</> : <><RefreshCw className="h-4 w-4 mr-2" />Run agent</>}
        </Button>
      </PageHeader>

      {/* Agent narrative */}
      <div className="ai-surface p-5">
        <div className="flex items-center gap-2 mb-2"><AgentChip agent="risk" label="Retrospective" /></div>
        <p className="text-[15px] text-slate-800 leading-relaxed">{narrative}</p>
        <div className="mt-3 text-[11px] text-slate-400">Curated from {a.total} eligibility responses · PHI excluded · illustrative</div>
      </div>

      {/* KPI metrics with sparklines */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Metric label="Auto-cleared" value={pct(a.autoClearRate)} sub="no human review" data={trend} dataKey="autoPct" color="#0e7490" />
        <Metric label="Active coverage" value={pct(a.activeRate)} sub={`${pct(a.inactiveRate)} inactive`} tone="text-emerald-700" data={trend} dataKey="checks" color="#10b981" />
        <Metric label="Out-of-network" value={pct(netPct.out)} sub="higher cost-share" tone="text-rose-600" data={trend} dataKey="oonPct" color="#f43f5e" />
        <Metric label="Flagged to collect" value={`$${(trend[trend.length - 1]?.collected || 0).toFixed(1)}k`} sub="this week" tone="text-brand-700" data={trend} dataKey="collected" color="#27b2c6" />
      </div>

      {/* Hero area chart */}
      <div className="dh-card p-5">
        <div className="flex items-center justify-between mb-3">
          <div><div className="text-sm font-semibold text-slate-900">Eligibility volume</div><div className="text-xs text-slate-500">auto-cleared vs. needs attention · per week</div></div>
          <div className="flex items-center gap-3 text-[11px]">
            <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: '#0e7490' }} />Auto-cleared</span>
            <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: '#f59e0b' }} />Needs attention</span>
          </div>
        </div>
        <div className="h-[260px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={trend} margin={{ top: 4, right: 8, bottom: 0, left: -16 }}>
              <defs>
                <linearGradient id="gCleared" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#0e7490" stopOpacity={0.35} /><stop offset="100%" stopColor="#0e7490" stopOpacity={0.02} /></linearGradient>
                <linearGradient id="gAttn" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#f59e0b" stopOpacity={0.35} /><stop offset="100%" stopColor="#f59e0b" stopOpacity={0.02} /></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef0f5" vertical={false} />
              <XAxis dataKey="week" tick={axis} axisLine={false} tickLine={false} />
              <YAxis tick={axis} axisLine={false} tickLine={false} width={40} />
              <Tooltip contentStyle={tip} labelStyle={{ color: '#64748b', marginBottom: 2 }} />
              <Area type="monotone" dataKey="cleared" stackId="1" stroke="#0e7490" strokeWidth={2} fill="url(#gCleared)" name="Auto-cleared" />
              <Area type="monotone" dataKey="attention" stackId="1" stroke="#f59e0b" strokeWidth={2} fill="url(#gAttn)" name="Needs attention" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <Story>
          <b className="text-slate-800">Auto-clear {autoDelta >= 0 ? 'climbed' : 'slipped'} {Math.abs(autoDelta)} pts</b> over {weeks} weeks ({t0.autoPct || 0}% → {tN.autoPct || 0}%). About <b className="text-slate-800">{avgAttention} visits/week</b> still need a human — that's the front-desk load to staff around, and the rest clears itself.
        </Story>
      </div>

      {/* Distributions */}
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="dh-card p-5 lg:col-span-2">
          <div className="text-sm font-semibold text-slate-900 mb-3">Plan types returned</div>
          <div className="h-[220px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={planData} margin={{ top: 4, right: 8, bottom: 0, left: -16 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef0f5" vertical={false} />
                <XAxis dataKey="name" tick={axis} axisLine={false} tickLine={false} interval={0} />
                <YAxis tick={axis} axisLine={false} tickLine={false} width={40} allowDecimals={false} />
                <Tooltip contentStyle={tip} cursor={{ fill: '#f5f6fb' }} />
                <Bar dataKey="value" radius={[5, 5, 0, 0]} maxBarSize={46}>
                  {planData.map((d, i) => <Cell key={d.name} fill={PLAN_COLORS[i % PLAN_COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          {topPlan && (
            <Story>
              <b className="text-slate-800">{topPlan.name} is your most common plan</b> at {topShare}% of responses{secondPlan ? <>, then {secondPlan.name}</> : null}. {gatekeeperShare >= 25
                ? <>Gatekeeper plans (HMO/EPO) are <b className="text-slate-800">{gatekeeperShare}%</b> of volume — expect referral holds to drive most exceptions.</>
                : <>It's an open-access-heavy mix, so referrals rarely block a visit.</>}
            </Story>
          )}
        </div>
        <div className="dh-card p-5">
          <div className="text-sm font-semibold text-slate-900 mb-3">Network mix</div>
          <div className="h-[160px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={netData} dataKey="value" nameKey="label" cx="50%" cy="50%" innerRadius={42} outerRadius={64} paddingAngle={2} stroke="none">
                  {netData.map((d) => <Cell key={d.key} fill={d.color} />)}
                </Pie>
                <Tooltip contentStyle={tip} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-1.5 mt-1">
            {netData.map((s) => (
              <div key={s.key} className="flex items-center gap-2 text-xs"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: s.color }} /><span className="text-slate-600">{s.label}</span><span className="ml-auto font-semibold text-slate-900 tnum">{pct(netPct[s.key])}</span></div>
            ))}
          </div>
          <Story>
            <b className="text-slate-800">{pct(riskShare)} of responses</b> are out-of-network or unknown — the slice that carries higher cost-share or contracting risk. {net.unknown > 0
              ? <>The {net.unknown} <b className="text-slate-800">unknowns</b> are the quick win: add those plans to your contracted-plans DB to flip them in-network.</>
              : <>Coverage is almost all confirmed in-network.</>}
          </Story>
        </div>
      </div>

      {/* Payer scorecard */}
      <div className="dh-card-flush">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
          <div className="text-sm font-semibold text-slate-900">Payer scorecard</div>
          <div className="text-xs text-slate-500">where the front-desk effort goes</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-500 bg-slate-50/70"><tr>{['Payer', 'Checks', 'Out-of-network', 'Referral req.', 'Prior auth', 'Avg copay'].map((h, i) => <th key={i} className={`font-medium px-4 py-2.5 ${i === 0 ? 'text-left' : 'text-right'}`}>{h}</th>)}</tr></thead>
            <tbody>
              {byPayer.map((p) => (
                <tr key={p.name} className="border-t border-slate-100">
                  <td className="px-4 py-2.5 text-slate-900 font-medium">{p.name}</td>
                  <td className="px-4 py-2.5 text-right tnum text-slate-700">{p.count}</td>
                  <td className={`px-4 py-2.5 text-right tnum ${p.oonRate > 0 ? 'text-rose-600' : 'text-slate-400'}`}>{pct(p.oonRate)}</td>
                  <td className={`px-4 py-2.5 text-right tnum ${p.refRate > 0 ? 'text-amber-600' : 'text-slate-400'}`}>{pct(p.refRate)}</td>
                  <td className={`px-4 py-2.5 text-right tnum ${p.authRate > 0 ? 'text-amber-600' : 'text-slate-400'}`}>{pct(p.authRate)}</td>
                  <td className="px-4 py-2.5 text-right tnum text-slate-700">{money(p.avgCopay)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {worstPayer && (
          <div className="px-5 py-3 border-t border-slate-100 flex items-start gap-2">
            <Lightbulb className="h-3.5 w-3.5 text-amber-500 mt-0.5 shrink-0" />
            <p className="text-[13px] text-slate-600 leading-snug">
              <b className="text-slate-800">{worstPayer.name} drives the most front-desk work</b> — {pct(worstPayer.oonRate)} out-of-network, {pct(worstPayer.refRate)} need a referral, {pct(worstPayer.authRate)} need prior auth. Focus contracting and referral automation there for the biggest drop in manual effort.
            </p>
          </div>
        )}
      </div>

      {/* Recurring denials — gated behind the Claims & Denials module */}
      {claimsEnabled && (
      <div>
        <div className="flex items-center gap-2 mb-2"><Sparkles className="h-4 w-4 text-violet-500" /><h2 className="text-sm font-semibold text-slate-900">Recurring denials</h2><span className="text-xs text-slate-500">turn the worst offenders into rules</span></div>
        <div className="grid md:grid-cols-2 gap-4">
          {patterns.map((d) => (
            <div key={d.id} className="dh-card p-5">
              <div className="flex items-start justify-between">
                <div><div className="font-semibold text-slate-900">{d.planName}</div><div className="text-xs text-slate-500">{d.payerName}{d.cpt && <> · CPT {d.cpt}</>} · last seen {d.lastSeen}</div></div>
                <div className="text-right"><div className="text-2xl font-semibold text-rose-600 tnum">{d.frequency}</div><div className="text-[10px] text-slate-500 uppercase">denials</div></div>
              </div>
              <div className="mt-3 text-sm text-slate-700">{d.denialReason}</div>
              <div className="ai-surface p-3 mt-3 text-xs text-slate-800"><div className="flex items-center gap-1.5 font-semibold text-violet-700 mb-0.5"><Bot className="h-3 w-3" />Recommended rule</div>{d.recommendedAction}</div>
              <div className="mt-3">
                {applied[d.id]
                  ? <span className="inline-flex items-center gap-1.5 text-xs text-emerald-700 font-medium"><Zap className="h-3.5 w-3.5" />Rule active</span>
                  : <Button size="sm" variant="outline" onClick={() => applyRule(d.id)}><Zap className="h-3.5 w-3.5 mr-1.5" />Apply as rule <ArrowRight className="h-3.5 w-3.5 ml-1" /></Button>}
              </div>
            </div>
          ))}
        </div>
      </div>
      )}
    </div>
  );
}
